import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import {
  BottomSheet,
  LoadingSpinner,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback, useEffect, useRef } from "react";

export interface EarningsPageOptionsBottomSheetProps {
  modalState: UseModalState;
  isGoToStripeLoading: boolean;
  onViewTaxInfo: () => void;
  onGoToStripe: () => void;
  onGoToProofOfEarnings: () => void;
  onGoToPayoutSettings: () => void;
}

export function EarningsPageOptionsBottomSheet(props: EarningsPageOptionsBottomSheetProps) {
  const {
    modalState,
    isGoToStripeLoading,
    onViewTaxInfo,
    onGoToStripe,
    onGoToProofOfEarnings,
    onGoToPayoutSettings,
  } = props;
  const isGoingToStripe = useRef(false);

  useEffect(() => {
    if (modalState.modalIsClosed) {
      isGoingToStripe.current = false;
    }
  }, [modalState.modalIsClosed]);

  useEffect(() => {
    // user clicked to go to stripe, and loading finished
    // -> auto-close sheet
    if (!isGoToStripeLoading && isGoingToStripe.current) {
      modalState.closeModal();
    }
  }, [isGoToStripeLoading, modalState]);

  const onExportEarningsClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_SETTINGS_EXPORT_EARNINGS_CLICKED);
    onGoToProofOfEarnings();
  }, [onGoToProofOfEarnings]);

  const onViewTaxInfoClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_SETTINGS_TAX_INFO_CLICKED);
    onViewTaxInfo();
  }, [onViewTaxInfo]);

  const onGoToPayoutSettingsClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_SETTINGS_PAYOUT_SETTINGS_CLICKED);
    onGoToPayoutSettings();
  }, [onGoToPayoutSettings]);

  const onGoToStripeClick = useCallback(() => {
    isGoingToStripe.current = true;

    logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_SETTINGS_STRIPE_CLICKED);
    onGoToStripe();
  }, [onGoToStripe]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
    >
      <Stack spacing={4} sx={(theme) => ({ padding: theme.spacing(4) })}>
        <Button variant="outlined" onClick={onExportEarningsClick}>
          Export earnings
        </Button>
        <Button variant="outlined" onClick={onViewTaxInfoClick}>
          View tax info
        </Button>
        <Button variant="outlined" onClick={onGoToPayoutSettingsClick}>
          Go to payout settings
        </Button>
        <Button disabled={isGoToStripeLoading} variant="outlined" onClick={onGoToStripeClick}>
          {isGoToStripeLoading ? <LoadingSpinner size="small" /> : "Go to Stripe"}
        </Button>
      </Stack>
    </BottomSheet>
  );
}
